<template>
  <div class="py-6">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p class="text-sm font-medium text-blue-700 dark:text-blue-300">Platform overview</p>
          <h1 class="mt-1 text-3xl font-semibold text-gray-950 dark:text-white">DeepChat Admin</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
            Manage customer access, billing status, AI model assignments, email templates, payments, and SMS delivery from one place.
          </p>
        </div>
        <button
          @click="runBillingSchedulerNow"
          class="inline-flex items-center justify-center gap-2 rounded-md border border-amber-300 bg-white px-4 py-2 text-sm font-medium text-amber-800 shadow-sm hover:bg-amber-50 dark:border-amber-700 dark:bg-gray-800 dark:text-amber-200 dark:hover:bg-amber-900/20"
        >
          <RefreshCw class="h-4 w-4" />
          Run Billing Check
        </button>
      </div>

      <div class="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div
          v-for="metric in adminMetrics"
          :key="metric.label"
          class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900"
        >
          <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ metric.label }}</p>
          <div class="mt-2 flex items-end justify-between gap-3">
            <p class="text-2xl font-semibold text-gray-950 dark:text-white">{{ metric.value }}</p>
            <span :class="['rounded-full px-2 py-1 text-xs font-medium', metric.badgeClass]">
              {{ metric.badge }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-6 md:hidden">
      <div class="overflow-x-auto rounded-lg border border-gray-200 bg-white p-1 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <nav class="flex min-w-max gap-1">
          <button
            v-for="tab in adminTabs"
            :key="tab.id"
            @click="setActiveTab(tab.id)"
            :class="[
              activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-950 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white',
              'inline-flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors'
            ]"
          >
            <component :is="tab.icon" class="h-4 w-4" />
            {{ tab.label }}
          </button>
        </nav>
      </div>
    </div>

    <!-- Models Tab -->
    <div v-if="activeTab === 'models'" class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-8">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">AI Models</h2>
        <button
          @click="showAddModelModal = true"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
          </svg>
          Add Model
        </button>
      </div>

      <!-- Models List -->
      <div v-if="modelsLoading" class="flex justify-center py-12">
        <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <div v-else-if="models.length === 0" class="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
        <p class="text-gray-500 dark:text-gray-400">No models configured. Add your first AI model.</p>
      </div>

      <div v-else class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="model in models"
          :key="model.id"
          class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ model.name }}</h3>
              <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">{{ model.provider }}</p>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-500">{{ model.model_identifier }}</p>
              <p v-if="model.description" class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {{ model.description }}
              </p>
            </div>
            <div class="flex flex-col items-end gap-2">
              <span
                :class="[
                  getModelApiStatusClass(model),
                  'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium'
                ]"
                :title="model.api_status_message || ''"
              >
                <span :class="['h-2 w-2 rounded-full', getModelApiDotClass(model)]"></span>
                {{ getModelApiStatusLabel(model) }}
              </span>
              <span
                :class="[
                  model.is_active
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
                ]"
              >
                {{ model.is_active ? 'Enabled' : 'Disabled' }}
              </span>
            </div>
          </div>
          <p class="mt-3 text-xs" :class="getModelApiMessageClass(model)">
            {{ model.api_status_message || 'API status not checked yet' }}
          </p>
          <div class="mt-4 flex space-x-2">
            <button
              @click="editModel(model)"
              class="flex-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Edit
            </button>
            <button
              @click="confirmDeleteModel(model)"
              class="flex-1 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-white dark:bg-gray-700 border border-red-300 dark:border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Companies Tab -->
    <div v-if="activeTab === 'companies'" class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-8">
      <div class="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Companies</h2>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Find a customer, update their limits, assign their AI model, or access their dashboard.
          </p>
        </div>
        <div class="flex flex-col gap-2 sm:flex-row">
          <label class="relative block">
            <Search class="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              v-model="companySearch"
              type="search"
              placeholder="Search company, owner, domain"
              class="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white sm:w-72"
            />
          </label>
          <select
            v-model="companyStatusFilter"
            class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="needs_attention">Needs attention</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div v-if="companiesLoading" class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <div v-for="item in 6" :key="item" class="h-48 animate-pulse rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div class="h-4 w-2/3 rounded bg-gray-200 dark:bg-gray-800"></div>
          <div class="mt-3 h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-800"></div>
          <div class="mt-8 h-10 rounded bg-gray-200 dark:bg-gray-800"></div>
          <div class="mt-4 h-10 rounded bg-gray-200 dark:bg-gray-800"></div>
        </div>
      </div>

      <div v-else-if="companies.length === 0" class="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
        <Building2 class="mx-auto h-10 w-10 text-gray-400" />
        <h3 class="mt-3 text-base font-semibold text-gray-900 dark:text-white">No companies yet</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Registered companies will appear here when customers sign up.</p>
      </div>

      <div v-else-if="filteredCompanies.length === 0" class="rounded-lg border border-gray-200 bg-white p-10 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <SearchX class="mx-auto h-10 w-10 text-gray-400" />
        <h3 class="mt-3 text-base font-semibold text-gray-900 dark:text-white">No matching companies</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Try another search term or status filter.</p>
      </div>

      <div v-else class="space-y-3">
        <div class="text-sm text-gray-600 dark:text-gray-400">
          Showing {{ filteredCompanies.length }} of {{ companies.length }} companies
        </div>
        <div class="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <article
            v-for="company in filteredCompanies"
            :key="company.id"
            class="flex min-h-[360px] flex-col rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <h3 class="truncate text-lg font-semibold text-gray-950 dark:text-white">{{ company.name }}</h3>
                <p class="truncate text-sm text-gray-500 dark:text-gray-400">{{ company.domain }}</p>
                <p v-if="company.industry" class="mt-1 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                  {{ company.industry }}
                </p>
              </div>
              <span class="inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-medium" :class="getCompanyStatusClass(company)">
                {{ getCompanyStatusLabel(company) }}
              </span>
            </div>

            <dl class="mt-5 grid grid-cols-2 gap-3 text-sm">
              <div class="rounded-md bg-gray-50 p-3 dark:bg-gray-800/70">
                <dt class="text-xs font-medium uppercase text-gray-400 dark:text-gray-500">Owner</dt>
                <dd class="mt-1 truncate font-medium text-gray-900 dark:text-white">{{ company.owner_name || 'N/A' }}</dd>
                <dd class="truncate text-xs text-gray-500 dark:text-gray-400">{{ company.owner_email || 'N/A' }}</dd>
              </div>
              <div class="rounded-md bg-gray-50 p-3 dark:bg-gray-800/70">
                <dt class="text-xs font-medium uppercase text-gray-400 dark:text-gray-500">Billing</dt>
                <dd class="mt-1 truncate font-medium text-gray-900 dark:text-white">{{ company.billing_email || company.owner_email || 'N/A' }}</dd>
                <dd class="truncate text-xs text-gray-500 dark:text-gray-400">Ends {{ formatDate(company.billing_period_end || company.trial_end_date) }}</dd>
              </div>
              <div class="rounded-md bg-gray-50 p-3 dark:bg-gray-800/70">
                <dt class="text-xs font-medium uppercase text-gray-400 dark:text-gray-500">Agents</dt>
                <dd class="mt-1 font-medium text-gray-900 dark:text-white">
                  {{ company.current_agent_count || 0 }} of {{ formatAgentLimit(company.max_agents) }}
                </dd>
              </div>
              <div class="rounded-md bg-gray-50 p-3 dark:bg-gray-800/70">
                <dt class="text-xs font-medium uppercase text-gray-400 dark:text-gray-500">Model</dt>
                <dd class="mt-1 truncate font-medium" :class="company.model_name ? 'text-gray-900 dark:text-white' : 'text-red-600 dark:text-red-300'">
                  {{ company.model_name || 'Not assigned' }}
                </dd>
              </div>
            </dl>

            <div class="mt-4 grid gap-3 sm:grid-cols-2">
              <label class="block">
                <span class="text-xs font-medium uppercase text-gray-400 dark:text-gray-500">AI Model</span>
                <select
                  v-if="models.length > 0"
                  :value="company.model_id || ''"
                  @change="(e) => setCompanyModel(company.id, (e.target as HTMLSelectElement).value)"
                  class="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                >
                  <option value="">No Model</option>
                  <option v-for="model in models" :key="model.id" :value="model.id">
                    {{ model.name }}
                  </option>
                </select>
                <div v-else class="mt-1 rounded-md border border-dashed border-red-300 px-3 py-2 text-sm text-red-600 dark:border-red-800 dark:text-red-300">
                  Add an AI model first
                </div>
              </label>
              <label class="block">
                <span class="text-xs font-medium uppercase text-gray-400 dark:text-gray-500">Agent limit</span>
                <select
                  :value="company.max_agents ?? 5"
                  @change="(e) => updateMaxAgents(company, parseInt((e.target as HTMLSelectElement).value))"
                  class="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                >
                  <option :value="3">3</option>
                  <option :value="5">5</option>
                  <option :value="10">10</option>
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                  <option :value="0">Unlimited</option>
                </select>
              </label>
            </div>

            <div class="mt-auto pt-5">
              <div class="grid grid-cols-2 gap-2">
                <button @click="openCompanyEditor(company)" class="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
                  <PencilLine class="h-4 w-4" />
                  Edit
                </button>
                <button @click="impersonateCompany(company)" class="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700">
                  <LogIn class="h-4 w-4" />
                  Access
                </button>
                <button
                  @click="toggleCompanyActive(company)"
                  class="inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium"
                  :class="company.is_active === false
                    ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300'
                    : 'border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300'"
                >
                  {{ company.is_active === false ? 'Activate' : 'Deactivate' }}
                </button>
                <button @click="createCheckout(company)" class="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
                  Stripe
                </button>
                <button @click="markCompanyPaid(company)" class="rounded-md border border-emerald-300 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-900/20">
                  Mark paid
                </button>
                <button
                  v-if="company.subscription_status === 'trial'"
                  @click="sendTrialReminder(company)"
                  class="rounded-md border border-amber-300 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/20"
                >
                  Upgrade
                </button>
                <button
                  v-else
                  @click="sendRenewalReminder(company)"
                  class="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  Reminder
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>

    <!-- Company Editor Modal -->
    <div
      v-if="editingCompany"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8"
      @click.self="closeCompanyEditor"
    >
      <div class="w-full max-w-3xl rounded-lg bg-white p-6 shadow-xl dark:bg-gray-900">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="text-xl font-semibold text-gray-950 dark:text-white">Edit company</h2>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Update company profile, billing contact and owner details.
            </p>
          </div>
          <button
            type="button"
            class="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            @click="closeCompanyEditor"
          >
            Close
          </button>
        </div>

        <div class="mt-6 grid gap-4 md:grid-cols-2">
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Company name</span>
            <input v-model="companyEditForm.name" type="text" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Domain</span>
            <input v-model="companyEditForm.domain" type="text" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Industry</span>
            <input v-model="companyEditForm.industry" type="text" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Owner email</span>
            <input v-model="companyEditForm.owner_email" type="email" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Owner first name</span>
            <input v-model="companyEditForm.owner_first_name" type="text" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Owner last name</span>
            <input v-model="companyEditForm.owner_last_name" type="text" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Billing email</span>
            <input v-model="companyEditForm.billing_email" type="email" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Agent limit</span>
            <select v-model.number="companyEditForm.max_agents" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
              <option :value="0">Unlimited</option>
              <option :value="3">3</option>
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
            </select>
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Subscription status</span>
            <select v-model="companyEditForm.subscription_status" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
              <option value="trial">Trial</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
        </div>

        <div class="mt-4 flex items-center gap-2">
          <input id="company-active" v-model="companyEditForm.is_active" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          <label for="company-active" class="text-sm text-gray-700 dark:text-gray-300">Company active</label>
        </div>

        <div class="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            class="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            @click="closeCompanyEditor"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            @click="saveCompanyDetails"
          >
            Save changes
          </button>
        </div>
      </div>
    </div>

    <!-- Email Settings Tab -->
    <div v-if="activeTab === 'email'" class="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 mt-8 space-y-6">
      <div class="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Email Delivery</h2>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Configure the default sender and manage SMTP servers for platform email delivery.
            </p>
          </div>
          <span
            :class="[
              emailSettings.is_active
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
              'inline-flex w-fit rounded-full px-3 py-1 text-xs font-medium'
            ]"
          >
            {{ emailSettings.is_active ? 'Sending enabled' : 'Sending disabled' }}
          </span>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">From email</span>
            <input v-model="emailSettings.from_email" type="email" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">From name</span>
            <input v-model="emailSettings.from_name" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Reply-to email</span>
            <input v-model="emailSettings.reply_to_email" type="email" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">SendGrid API key</span>
            <input v-model="emailSettings.sendgrid_api_key" type="password" placeholder="Leave blank to keep existing key" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">SMTP host</span>
            <input v-model="emailSettings.smtp_host" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">SMTP port</span>
            <input v-model.number="emailSettings.smtp_port" type="number" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="flex items-center space-x-2">
            <input v-model="emailSettings.is_active" type="checkbox" class="rounded border-gray-300 text-blue-600" />
            <span class="text-sm text-gray-700 dark:text-gray-300">Enable email sending</span>
          </label>
        </div>
        <div class="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <button @click="saveEmailSettings" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Save Email Settings</button>
          <input v-model="testEmailTo" type="email" placeholder="test@example.com" class="px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          <button @click="sendTestEmail" class="px-4 py-2 border rounded-md dark:border-gray-600 dark:text-white">Send Test</button>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,420px)_1fr]">
        <div class="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div class="flex items-center justify-between gap-3">
            <div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                {{ editingSmtpServer ? 'Edit SMTP server' : 'Add SMTP server' }}
              </h3>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Store sender, host, port, login, and activation state.</p>
            </div>
            <button
              v-if="editingSmtpServer"
              @click="cancelEditSmtpServer"
              class="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
          </div>

          <div class="mt-5 grid grid-cols-1 gap-4">
            <label class="block">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Display name</span>
              <input v-model="smtpForm.name" placeholder="Production SMTP" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
            </label>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_110px]">
              <label class="block">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">SMTP host</span>
                <input v-model="smtpForm.host" placeholder="smtp.example.com" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
              </label>
              <label class="block">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Port</span>
                <input v-model.number="smtpForm.port" type="number" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
              </label>
            </div>
            <label class="block">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Encryption</span>
              <select v-model="smtpForm.encryption" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white">
                <option value="starttls">STARTTLS</option>
                <option value="tls">TLS</option>
                <option value="none">None</option>
              </select>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Username</span>
              <input v-model="smtpForm.username" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Password</span>
              <input v-model="smtpForm.password" type="password" :placeholder="editingSmtpServer?.has_password ? 'Saved - leave blank to keep' : ''" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
            </label>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label class="block">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">From email</span>
                <input v-model="smtpForm.from_email" type="email" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
              </label>
              <label class="block">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">From name</span>
                <input v-model="smtpForm.from_name" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
              </label>
            </div>
            <label class="block">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Reply-to email</span>
              <input v-model="smtpForm.reply_to_email" type="email" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
            </label>
            <label class="flex items-center gap-2">
              <input v-model="smtpForm.is_active" type="checkbox" class="rounded border-gray-300 text-blue-600" />
              <span class="text-sm text-gray-700 dark:text-gray-300">Activate this server after saving</span>
            </label>
          </div>

          <button @click="saveSmtpServer" class="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            <Plus class="h-4 w-4" />
            {{ editingSmtpServer ? 'Update SMTP Server' : 'Add SMTP Server' }}
          </button>
        </div>

        <div class="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">SMTP servers</h3>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Only one server can be active at a time.</p>
            </div>
            <button @click="loadSmtpServers" class="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400">Refresh</button>
          </div>

          <div v-if="smtpServers.length === 0" class="mt-6 rounded-lg border border-dashed border-gray-300 p-8 text-center dark:border-gray-700">
            <Server class="mx-auto h-10 w-10 text-gray-400" />
            <h4 class="mt-3 text-sm font-semibold text-gray-900 dark:text-white">No SMTP servers added</h4>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Add a server to make it available for platform email settings.</p>
          </div>

          <div v-else class="mt-5 grid gap-4">
            <article
              v-for="server in smtpServers"
              :key="server.id"
              class="rounded-lg border p-4"
              :class="server.is_active ? 'border-emerald-300 bg-emerald-50/40 dark:border-emerald-800 dark:bg-emerald-950/20' : 'border-gray-200 dark:border-gray-800'"
            >
              <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <h4 class="truncate text-base font-semibold text-gray-950 dark:text-white">{{ server.name }}</h4>
                    <span :class="['rounded-full px-2.5 py-0.5 text-xs font-medium', server.is_active ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300']">
                      {{ server.is_active ? 'Active' : 'Inactive' }}
                    </span>
                    <span class="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                      {{ server.encryption.toUpperCase() }}
                    </span>
                  </div>
                  <p class="mt-1 font-mono text-sm text-gray-600 dark:text-gray-400">{{ server.host }}:{{ server.port }}</p>
                  <p class="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">{{ server.from_name }} &lt;{{ server.from_email }}&gt;</p>
                  <p class="truncate text-xs text-gray-400 dark:text-gray-500">User: {{ server.username }} · Password {{ server.has_password ? 'saved' : 'missing' }}</p>
                </div>

                <div class="grid grid-cols-2 gap-2 sm:w-56">
                  <button
                    @click="server.is_active ? deactivateSmtpServer(server) : activateSmtpServer(server)"
                    class="inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium"
                    :class="server.is_active ? 'border-gray-300 text-gray-700 hover:bg-white dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300'"
                  >
                    <Power class="h-4 w-4" />
                    {{ server.is_active ? 'Disable' : 'Activate' }}
                  </button>
                  <button @click="editSmtpServer(server)" class="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
                    Edit
                  </button>
                  <button @click="deleteSmtpServer(server)" class="col-span-2 rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/20">
                    Delete
                  </button>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>

    <!-- Templates Tab -->
    <div v-if="activeTab === 'templates'" class="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 mt-8">
      <div class="space-y-4">
        <div v-for="template in emailTemplates" :key="template.template_key" class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ template.name }}</h3>
            <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input v-model="template.is_active" type="checkbox" class="rounded border-gray-300 text-blue-600" />
              Active
            </label>
          </div>
          <label class="block mb-3">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Subject</span>
            <input v-model="template.subject" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="block mb-3">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">HTML body</span>
            <textarea v-model="template.html_body" rows="5" class="mt-1 w-full px-3 py-2 border rounded-md font-mono text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"></textarea>
          </label>
          <label class="block mb-3">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Text body</span>
            <textarea v-model="template.text_body" rows="3" class="mt-1 w-full px-3 py-2 border rounded-md font-mono text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"></textarea>
          </label>
          <label class="block mb-4">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">SMS body</span>
              <span class="text-xs text-gray-400 dark:text-gray-500">({{ (template.sms_body || '').length }} / 160 chars)</span>
            </div>
            <textarea v-model="template.sms_body" rows="2" maxlength="500" placeholder="SMS version of this template (optional)" class="mt-1 w-full px-3 py-2 border rounded-md font-mono text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"></textarea>
          </label>
          <button @click="saveTemplate(template)" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Save Template</button>
        </div>
      </div>
    </div>

    <!-- Payments Tab -->
    <div v-if="activeTab === 'payments'" class="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 mt-8 space-y-6">
      <!-- Public Checkout Readiness -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Public Checkout Readiness</h2>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Controls whether customers can buy DeepChat directly from the public page.
            </p>
          </div>
          <span
            class="inline-flex w-fit items-center rounded-full px-3 py-1 text-sm font-medium"
            :class="paymentReadinessIssues.length === 0 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'"
          >
            {{ paymentReadinessIssues.length === 0 ? 'Ready for public purchases' : 'Setup needed' }}
          </span>
        </div>
        <div v-if="paymentReadinessIssues.length === 0" class="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
          Public checkout can create orders, open Stripe Checkout, activate paid companies after verification, and send the configured purchase confirmation email.
        </div>
        <ul v-else class="mt-4 space-y-2 text-sm text-amber-800 dark:text-amber-300">
          <li v-for="issue in paymentReadinessIssues" :key="issue" class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/20">
            {{ issue }}
          </li>
        </ul>
        <div class="mt-5 grid gap-3 sm:grid-cols-3">
          <div class="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <p class="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Paid revenue</p>
            <p class="mt-1 text-2xl font-semibold text-gray-950 dark:text-white">{{ formatCurrency(paidRevenueSek) }}</p>
          </div>
          <div class="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <p class="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Paid orders</p>
            <p class="mt-1 text-2xl font-semibold text-gray-950 dark:text-white">{{ paidOrders.length }}</p>
          </div>
          <div class="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <p class="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Pending orders</p>
            <p class="mt-1 text-2xl font-semibold text-gray-950 dark:text-white">{{ pendingOrders.length }}</p>
          </div>
        </div>
      </div>

      <!-- Public Plan Catalog -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Public Plan Catalog</h2>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Create and update the plans shown on the DeepChat public page.
            </p>
          </div>
          <button
            @click="startCreatePlan"
            class="inline-flex w-fit items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus class="h-4 w-4" />
            Add Plan
          </button>
        </div>

        <form v-if="showPlanForm" class="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20" @submit.prevent="savePlan">
          <div class="grid gap-4 md:grid-cols-2">
            <label class="block">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Plan name</span>
              <input v-model="planForm.name" required class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder="Business" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Stripe price ID</span>
              <input v-model="planForm.stripe_price_id" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder="price_..." />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Price SEK</span>
              <input v-model.number="planForm.price_sek" required type="number" min="0" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Billing period</span>
              <select v-model="planForm.billing_period" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Max agents</span>
              <input v-model.number="planForm.max_agents" type="number" min="0" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Sort order</span>
              <input v-model.number="planForm.sort_order" type="number" min="0" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </label>
          </div>
          <label class="mt-4 block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Description</span>
            <textarea v-model="planForm.description" rows="2" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder="For growing companies..."></textarea>
          </label>
          <label class="mt-4 block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Features, one per line</span>
            <textarea v-model="planFeaturesText" rows="5" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder="Multiple AI agents&#10;Conversation analytics"></textarea>
          </label>
          <label class="mt-4 flex items-center gap-2">
            <input v-model="planForm.is_active" type="checkbox" class="rounded border-gray-300 text-blue-600" />
            <span class="text-sm text-gray-700 dark:text-gray-300">Show on public page</span>
          </label>
          <div class="mt-5 flex flex-wrap gap-2">
            <button type="submit" :disabled="plansSaving" class="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
              {{ plansSaving ? 'Saving...' : editingPlanId ? 'Save Plan' : 'Create Plan' }}
            </button>
            <button type="button" @click="cancelPlanEdit" class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
              Cancel
            </button>
          </div>
        </form>

        <div v-if="plansLoading" class="mt-6 rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          Loading plans...
        </div>
        <div v-else-if="plans.length === 0" class="mt-6 rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          No plans yet. Add the first public plan.
        </div>

        <div v-else class="mt-6 grid gap-4 lg:grid-cols-3">
          <article
            v-for="plan in plans"
            :key="plan.id"
            class="rounded-lg border p-4"
            :class="plan.is_active ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20' : 'border-gray-200 bg-gray-50 opacity-75 dark:border-gray-700 dark:bg-gray-900/50'"
          >
            <div class="flex items-center justify-between gap-3">
              <h3 class="font-semibold text-gray-950 dark:text-white">{{ plan.name }}</h3>
              <span class="rounded-full px-2 py-0.5 text-xs font-medium" :class="plan.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'">
                {{ plan.is_active ? 'Public' : 'Hidden' }}
              </span>
            </div>
            <p class="mt-3 text-2xl font-semibold text-gray-950 dark:text-white">
              {{ plan.price_sek }} kr
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">/ {{ plan.billing_period === 'yearly' ? 'year' : 'month' }}</span>
            </p>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Max agents: {{ plan.max_agents === 0 ? 'Unlimited' : plan.max_agents }} · Sort: {{ plan.sort_order }} · Companies: {{ plan.company_count || 0 }}</p>
            <p class="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-400">{{ plan.description }}</p>
            <p v-if="plan.is_active && plan.price_sek > 0 && !plan.stripe_price_id" class="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              Missing Stripe Price ID. Customers cannot buy this plan publicly until it is set.
            </p>
            <ul class="mt-4 space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <li v-for="feature in plan.features" :key="feature" class="flex gap-2">
                <Check class="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-300" />
                <span>{{ feature }}</span>
              </li>
            </ul>
            <p v-if="plan.stripe_price_id" class="mt-4 truncate rounded-md bg-white px-2 py-1 text-xs font-mono text-gray-500 dark:bg-gray-800 dark:text-gray-400">{{ plan.stripe_price_id }}</p>
            <div class="mt-5 flex gap-2">
              <button @click="startEditPlan(plan)" class="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">Edit</button>
              <button @click="deletePlan(plan)" class="rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-800 dark:bg-gray-800 dark:text-red-300 dark:hover:bg-red-900/20">Delete</button>
            </div>
          </article>
        </div>
      </div>

      <!-- Recent Orders -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Recent Public Orders</h2>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Orders created from the public checkout and verified after Stripe payment.
            </p>
          </div>
          <button @click="loadOrders" class="inline-flex w-fit items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
            <RefreshCw class="h-4 w-4" />
            Refresh
          </button>
        </div>
        <div v-if="ordersLoading" class="mt-6 rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          Loading orders...
        </div>
        <div v-else-if="orders.length === 0" class="mt-6 rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          No public orders yet.
        </div>
        <div v-else class="mt-6 overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead class="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th class="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">Order</th>
                <th class="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">Customer</th>
                <th class="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">Plan</th>
                <th class="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">Amount</th>
                <th class="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">Billing</th>
                <th class="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">Status</th>
                <th class="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">Created</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr v-for="order in orders" :key="order.id">
                <td class="px-3 py-3 align-top">
                  <p class="font-mono text-xs font-medium text-gray-900 dark:text-white">{{ order.order_number }}</p>
                  <p v-if="order.stripe_session_id" class="mt-1 max-w-32 truncate font-mono text-xs text-gray-400">{{ order.stripe_session_id }}</p>
                </td>
                <td class="px-3 py-3 align-top">
                  <p class="font-medium text-gray-900 dark:text-white">{{ order.company_name || 'Unknown company' }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ order.billing_email || order.owner_email || '-' }}</p>
                </td>
                <td class="px-3 py-3 align-top text-gray-700 dark:text-gray-300">
                  <p>{{ order.plan_name || 'No plan' }}</p>
                  <p v-if="order.promo_code" class="text-xs text-blue-600 dark:text-blue-300">Promo: {{ order.promo_code }}</p>
                </td>
                <td class="px-3 py-3 align-top font-medium text-gray-900 dark:text-white">{{ formatCurrency(order.amount_sek) }}</td>
                <td class="px-3 py-3 align-top text-gray-600 dark:text-gray-400">
                  <p>{{ order.billing_months }} month{{ order.billing_months === 1 ? '' : 's' }}</p>
                  <p class="text-xs">{{ formatDate(order.billing_start) }} - {{ formatDate(order.billing_end) }}</p>
                </td>
                <td class="px-3 py-3 align-top">
                  <span class="rounded-full px-2 py-0.5 text-xs font-medium" :class="order.status === 'paid' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : order.status === 'pending' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'">
                    {{ order.status }}
                  </span>
                  <p v-if="order.paid_at" class="mt-1 text-xs text-gray-500 dark:text-gray-400">Paid {{ formatDate(order.paid_at) }}</p>
                </td>
                <td class="px-3 py-3 align-top text-gray-500 dark:text-gray-400">{{ formatDate(order.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Mode Toggle -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Stripe Payments</h2>
          <span
            :class="[
              paymentSettings.stripe_mode === 'live'
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
              'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium'
            ]"
          >
            {{ paymentSettings.stripe_mode === 'live' ? 'LIVE' : 'TEST' }}
          </span>
        </div>
        <div class="flex items-center space-x-4">
          <span class="text-sm font-medium" :class="paymentSettings.stripe_mode === 'test' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'">Test</span>
          <button
            @click="paymentSettings.stripe_mode = paymentSettings.stripe_mode === 'test' ? 'live' : 'test'"
            :class="[
              paymentSettings.stripe_mode === 'live' ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600',
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            ]"
          >
            <span
              :class="[
                paymentSettings.stripe_mode === 'live' ? 'translate-x-5' : 'translate-x-0',
                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
              ]"
            ></span>
          </button>
          <span class="text-sm font-medium" :class="paymentSettings.stripe_mode === 'live' ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'">Live</span>
        </div>
        <p class="mt-3 text-sm" :class="paymentSettings.stripe_mode === 'live' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'">
          {{ paymentSettings.stripe_mode === 'live' ? 'Production mode -- real payments are being processed' : 'Test mode -- no real payments are processed' }}
        </p>
      </div>

      <!-- Test Keys -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow border p-6" :class="paymentSettings.stripe_mode === 'test' ? 'border-amber-300 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700 opacity-75'">
        <div class="flex items-center space-x-2 mb-4">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Test Keys</h3>
          <span v-if="paymentSettings.stripe_mode === 'test'" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">Active</span>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Publishable key (pk_test_...)</span>
            <input v-model="paymentSettings.stripe_test_publishable_key" type="text" placeholder="pk_test_..." class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Secret key (sk_test_...)</span>
            <input v-model="paymentSettings.stripe_test_secret_key" type="password" :placeholder="paymentSettings.has_stripe_test_secret_key ? 'Saved -- leave blank to keep' : 'sk_test_...'" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
        </div>
      </div>

      <!-- Live Keys -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow border p-6" :class="paymentSettings.stripe_mode === 'live' ? 'border-emerald-300 dark:border-emerald-700' : 'border-gray-200 dark:border-gray-700 opacity-75'">
        <div class="flex items-center space-x-2 mb-4">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Live Keys</h3>
          <span v-if="paymentSettings.stripe_mode === 'live'" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">Active</span>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Publishable key (pk_live_...)</span>
            <input v-model="paymentSettings.stripe_live_publishable_key" type="text" placeholder="pk_live_..." class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Secret key (sk_live_...)</span>
            <input v-model="paymentSettings.stripe_live_secret_key" type="password" :placeholder="paymentSettings.has_stripe_live_secret_key ? 'Saved -- leave blank to keep' : 'sk_live_...'" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
        </div>
      </div>

      <!-- General Settings -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">General Settings</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Webhook secret</span>
            <input v-model="paymentSettings.stripe_webhook_secret" type="password" :placeholder="paymentSettings.has_stripe_webhook_secret ? 'Saved -- leave blank to keep' : 'whsec_...'" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Default Stripe price ID</span>
            <input v-model="paymentSettings.default_price_id" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder="price_..." />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Trial days</span>
            <input v-model.number="paymentSettings.trial_days" type="number" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Renewal reminder days</span>
            <input v-model.number="paymentSettings.renewal_reminder_days" type="number" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="flex items-center space-x-2">
            <input v-model="paymentSettings.is_active" type="checkbox" class="rounded border-gray-300 text-blue-600" />
            <span class="text-sm text-gray-700 dark:text-gray-300">Enable Stripe payments</span>
          </label>
        </div>
        <div class="mt-6">
          <button @click="savePaymentSettings" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Save Payment Settings</button>
        </div>
      </div>
    </div>

    <!-- SMS Gateway Tab -->
    <div v-if="activeTab === 'sms'" class="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 mt-8 space-y-6">
      <!-- Add / Edit Provider Form -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {{ editingSmsProvider ? 'Edit SMS Provider' : 'Add SMS Provider' }}
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Provider name</span>
            <input v-model="smsForm.name" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder="e.g. GatewayAPI, 46elks" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">API URL</span>
            <input v-model="smsForm.api_url" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder="https://api.provider.com/sms" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">HTTP method</span>
            <select v-model="smsForm.http_method" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white">
              <option value="POST">POST</option>
              <option value="GET">GET</option>
            </select>
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Content type</span>
            <select v-model="smsForm.content_type" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white">
              <option value="application/json">JSON</option>
              <option value="application/x-www-form-urlencoded">Form</option>
            </select>
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Phone parameter</span>
            <input v-model="smsForm.phone_param" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder="phone" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Message parameter</span>
            <input v-model="smsForm.message_param" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder="message" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Phone prefix</span>
            <input v-model="smsForm.phone_prefix" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder="+46" />
          </label>
          <label class="block">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Priority (lower = first)</span>
            <input v-model.number="smsForm.priority" type="number" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="block md:col-span-2">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Authorization header</span>
            <input v-model="smsForm.auth_header" type="password" placeholder="Bearer token or Basic auth" class="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </label>
          <label class="block md:col-span-2">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Extra parameters (JSON)</span>
            <input v-model="smsForm.extra_params" class="mt-1 w-full px-3 py-2 border rounded-md font-mono text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder='{"api_key": "xxx"}' />
          </label>
          <label class="flex items-center space-x-2">
            <input v-model="smsForm.is_active" type="checkbox" class="rounded border-gray-300 text-blue-600" />
            <span class="text-sm text-gray-700 dark:text-gray-300">Active</span>
          </label>
        </div>
        <div class="mt-6 flex items-center gap-3">
          <button @click="saveSmsProvider" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            {{ editingSmsProvider ? 'Update Provider' : 'Add Provider' }}
          </button>
          <button v-if="editingSmsProvider" @click="cancelEditSmsProvider" class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:text-white">Cancel</button>
        </div>
      </div>

      <!-- Provider List -->
      <div v-if="smsProviders.length > 0" class="space-y-4">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Configured Providers</h3>
        <div v-for="provider in smsProviders" :key="provider.id" class="bg-white dark:bg-gray-800 rounded-lg shadow border p-5" :class="provider.is_active ? 'border-emerald-300 dark:border-emerald-700' : 'border-gray-200 dark:border-gray-700 opacity-60'">
          <div class="flex items-start justify-between">
            <div>
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full" :class="provider.is_active ? 'bg-emerald-500' : 'bg-gray-400'"></span>
                <h4 class="text-base font-semibold text-gray-900 dark:text-white">{{ provider.name }}</h4>
                <span class="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Priority {{ provider.priority }}</span>
              </div>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400 font-mono">{{ provider.http_method }} {{ provider.api_url }}</p>
              <div class="mt-1 flex items-center gap-4 text-xs text-gray-400 dark:text-gray-500">
                <span>Phone: <code>{{ provider.phone_param }}</code></span>
                <span>Msg: <code>{{ provider.message_param }}</code></span>
                <span>Prefix: {{ provider.phone_prefix }}</span>
              </div>
              <p v-if="provider.last_success_at" class="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Last success: {{ provider.last_success_at }}</p>
              <p v-if="provider.last_error" class="mt-1 text-xs text-red-600 dark:text-red-400">Error: {{ provider.last_error }}</p>
            </div>
          </div>
          <div class="mt-4 flex items-center gap-2">
            <button @click="editSmsProvider(provider)" class="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">Edit</button>
            <div class="flex items-center gap-1">
              <input v-model="testSmsPhone" type="text" placeholder="07XXXXXXXX" class="px-2 py-1.5 text-sm border rounded-md w-32 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              <button @click="testSms(provider)" class="px-3 py-1.5 text-sm text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-600 rounded-md hover:bg-amber-50 dark:hover:bg-amber-900/20">Test</button>
            </div>
            <button @click="deleteSmsProvider(provider)" class="px-3 py-1.5 text-sm text-red-700 dark:text-red-300 border border-red-300 dark:border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20">Delete</button>
          </div>
        </div>
      </div>

      <!-- SMS Logs -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">SMS Logs</h3>
          <button @click="loadSmsLogs" class="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400">Refresh</button>
        </div>
        <div v-if="smsLogs.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">No SMS logs yet</div>
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
            <thead class="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Time</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Provider</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Phone</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Message</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr v-for="log in smsLogs" :key="log.id">
                <td class="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">{{ log.created_at }}</td>
                <td class="px-3 py-2 text-gray-900 dark:text-white">{{ log.provider_name || '-' }}</td>
                <td class="px-3 py-2 text-gray-900 dark:text-white font-mono">{{ log.phone }}</td>
                <td class="px-3 py-2">
                  <span class="px-2 py-0.5 rounded-full text-xs font-medium" :class="log.status === 'sent' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'">{{ log.status }}</span>
                </td>
                <td class="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-xs truncate">{{ log.error_message || log.message }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add/Edit Model Modal -->
    <ModelModal
      :is-open="showAddModelModal || !!modelToEdit"
      :model="modelToEdit"
      @close="closeModelModal"
      @saved="loadModels"
    />

    <!-- Delete Model Confirmation -->
    <DeleteConfirmModal
      v-if="modelToDelete"
      :is-open="!!modelToDelete"
      title="Delete Model"
      :message="`Are you sure you want to delete ${modelToDelete?.name}?`"
      @confirm="handleDeleteModel"
      @cancel="modelToDelete = null"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import Swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'
import {
  Bot,
  Building2,
  Check,
  CreditCard,
  FileText,
  LogIn,
  Mail,
  MessageSquare,
  PencilLine,
  Plus,
  Power,
  RefreshCw,
  Search,
  SearchX,
  Server
} from 'lucide-vue-next'
import { useRoute, useRouter } from 'vue-router'
import { adminService, type AIModel, type Company, type EmailSettings, type EmailTemplate, type Order, type PaymentSettings, type SmsProvider, type SmsLog, type SmtpServer, type SubscriptionPlan, type SubscriptionPlanPayload, type UpdateCompanyDetailsRequest } from '../services/admin'
import ModelModal from '../components/ModelModal.vue'
import DeleteConfirmModal from '../components/DeleteConfirmModal.vue'

type AdminTab = 'models' | 'companies' | 'email' | 'templates' | 'payments' | 'sms'

const route = useRoute()
const router = useRouter()
const getRouteTab = (): AdminTab => {
  const tab = typeof route.query.tab === 'string' ? route.query.tab : 'companies'
  return ['models', 'companies', 'email', 'templates', 'payments', 'sms'].includes(tab) ? tab as AdminTab : 'companies'
}

const activeTab = ref<AdminTab>(getRouteTab())
const adminTabs = [
  { id: 'companies' as const, label: 'Companies', icon: Building2 },
  { id: 'models' as const, label: 'AI Models', icon: Bot },
  { id: 'email' as const, label: 'Email', icon: Mail },
  { id: 'templates' as const, label: 'Templates', icon: FileText },
  { id: 'payments' as const, label: 'Payments', icon: CreditCard },
  { id: 'sms' as const, label: 'SMS', icon: MessageSquare }
]
const models = ref<AIModel[]>([])
const companies = ref<Company[]>([])
const emailTemplates = ref<EmailTemplate[]>([])
const smtpServers = ref<SmtpServer[]>([])
const editingSmtpServer = ref<SmtpServer | null>(null)
const companySearch = ref('')
const companyStatusFilter = ref<'all' | 'active' | 'trial' | 'needs_attention' | 'inactive'>('all')
const modelsLoading = ref(false)
const companiesLoading = ref(false)
const editingCompany = ref<Company | null>(null)
const plans = ref<SubscriptionPlan[]>([])
const plansLoading = ref(false)
const plansSaving = ref(false)
const orders = ref<Order[]>([])
const ordersLoading = ref(false)
const showPlanForm = ref(false)
const editingPlanId = ref<number | null>(null)
const defaultPlanForm = (): SubscriptionPlanPayload => ({
  name: '',
  description: '',
  price_sek: 0,
  billing_period: 'monthly',
  max_agents: 5,
  features: [],
  stripe_price_id: '',
  is_active: true,
  sort_order: plans.value.length + 1
})
const planForm = ref<SubscriptionPlanPayload>(defaultPlanForm())
const planFeaturesText = ref('')
const showAddModelModal = ref(false)
const modelToEdit = ref<AIModel | null>(null)
const modelToDelete = ref<AIModel | null>(null)
const testEmailTo = ref('')
const emailSettings = ref<Partial<EmailSettings>>({
  provider: 'sendgrid',
  from_email: '',
  from_name: 'DeepChat',
  reply_to_email: '',
  smtp_host: 'smtp.sendgrid.net',
  smtp_port: 587,
  smtp_username: 'apikey',
  is_active: false
})
type SmtpServerForm = Omit<SmtpServer, 'id' | 'has_password' | 'created_at' | 'updated_at'> & { password?: string }

const defaultSmtpForm = (): SmtpServerForm => ({
  name: '',
  host: '',
  port: 587,
  username: '',
  password: '',
  encryption: 'starttls' as const,
  from_email: '',
  from_name: 'DeepChat',
  reply_to_email: '',
  is_active: false
})
const smtpForm = ref<SmtpServerForm>(defaultSmtpForm())
const paymentSettings = ref<Partial<PaymentSettings>>({
  stripe_mode: 'test',
  stripe_test_publishable_key: '',
  stripe_live_publishable_key: '',
  trial_days: 2,
  renewal_reminder_days: 7,
  is_active: false
})
const defaultCompanyEditForm = (): UpdateCompanyDetailsRequest => ({
  name: '',
  domain: '',
  industry: '',
  owner_first_name: '',
  owner_last_name: '',
  owner_email: '',
  billing_email: '',
  max_agents: 5,
  subscription_status: 'trial',
  is_active: true
})
const companyEditForm = ref<UpdateCompanyDetailsRequest>(defaultCompanyEditForm())

const isCompanyExpired = (company: Company) => {
  const expiryStr = company.billing_period_end || company.trial_end_date
  if (!expiryStr) return false
  return new Date(expiryStr).getTime() < Date.now()
}

const getCompanyStatusLabel = (company: Company) => {
  if (company.is_active === false || company.subscription_status === 'suspended') return 'Inactive'
  if (isCompanyExpired(company)) return 'Expired'
  if (company.subscription_status === 'trial') return 'Trial'
  if (company.subscription_status === 'active') return 'Active'
  return company.subscription_status || 'Unknown'
}

const getCompanyStatusClass = (company: Company) => {
  const status = getCompanyStatusLabel(company)
  if (status === 'Inactive' || status === 'Expired') return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  if (status === 'Trial') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
  if (status === 'Active') return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
  return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
}

const formatDate = (value?: string) => {
  if (!value) return 'N/A'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date)
}

const formatCurrency = (value?: number) => new Intl.NumberFormat('sv-SE', {
  style: 'currency',
  currency: 'SEK',
  maximumFractionDigits: 0
}).format(Number(value || 0))

const formatAgentLimit = (value?: number) => {
  if (value === 0) return 'Unlimited'
  return value ?? 5
}

const splitOwnerName = (value?: string | null) => {
  const trimmed = value?.trim() || ''
  if (!trimmed) return { first: '', last: '' }
  const parts = trimmed.split(/\s+/)
  return {
    first: parts.shift() || '',
    last: parts.join(' ')
  }
}

const companyMatchesFilter = (company: Company) => {
  if (companyStatusFilter.value === 'all') return true
  if (companyStatusFilter.value === 'inactive') {
    return company.is_active === false || company.subscription_status === 'suspended'
  }
  if (companyStatusFilter.value === 'needs_attention') {
    return isCompanyExpired(company) || !company.model_id || company.is_active === false || company.subscription_status === 'suspended'
  }
  return company.subscription_status === companyStatusFilter.value && company.is_active !== false
}

const filteredCompanies = computed(() => {
  const query = companySearch.value.trim().toLowerCase()
  return companies.value.filter((company) => {
    const matchesSearch = !query || [
      company.name,
      company.domain,
      company.industry,
      company.owner_name,
      company.owner_email,
      company.billing_email,
      company.model_name
    ].some((value) => value?.toLowerCase().includes(query))

    return matchesSearch && companyMatchesFilter(company)
  })
})

const adminMetrics = computed(() => {
  const activeCompanies = companies.value.filter((company) => company.is_active !== false && company.subscription_status === 'active').length
  const trialCompanies = companies.value.filter((company) => company.is_active !== false && company.subscription_status === 'trial').length
  const needsAttention = companies.value.filter((company) => companyMatchesAttention(company)).length
  const activeModels = models.value.filter((model) => model.is_active).length

  return [
    {
      label: 'Total companies',
      value: companies.value.length,
      badge: `${activeCompanies} active`,
      badgeClass: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
    },
    {
      label: 'Trials',
      value: trialCompanies,
      badge: 'Onboarding',
      badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
    },
    {
      label: 'Needs attention',
      value: needsAttention,
      badge: needsAttention > 0 ? 'Review' : 'Clear',
      badgeClass: needsAttention > 0
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
    },
    {
      label: 'Active AI models',
      value: activeModels,
      badge: `${models.value.length} total`,
      badgeClass: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
    }
  ]
})

const companyMatchesAttention = (company: Company) => {
  return isCompanyExpired(company) || !company.model_id || company.is_active === false || company.subscription_status === 'suspended'
}

const getModelApiStatusLabel = (model: AIModel) => {
  if (model.api_status === 'online') return 'API online'
  if (model.api_status === 'offline') return 'API offline'
  return 'API unknown'
}

const getModelApiStatusClass = (model: AIModel) => {
  if (model.api_status === 'online') {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
  }
  if (model.api_status === 'offline') {
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
}

const getModelApiDotClass = (model: AIModel) => {
  if (model.api_status === 'online') return 'bg-emerald-500'
  if (model.api_status === 'offline') return 'bg-red-500'
  return 'bg-gray-400'
}

const getModelApiMessageClass = (model: AIModel) => {
  if (model.api_status === 'online') return 'text-emerald-700 dark:text-emerald-300'
  if (model.api_status === 'offline') return 'text-red-700 dark:text-red-300'
  return 'text-gray-500 dark:text-gray-400'
}

const activePaidPlansMissingStripe = computed(() => (
  plans.value.filter((plan) => plan.is_active && Number(plan.price_sek) > 0 && !plan.stripe_price_id)
))

const paymentReadinessIssues = computed(() => {
  const issues: string[] = []
  const mode = paymentSettings.value.stripe_mode || 'test'
  const hasActiveKey = mode === 'live'
    ? Boolean(paymentSettings.value.has_stripe_live_secret_key || paymentSettings.value.stripe_live_secret_key)
    : Boolean(paymentSettings.value.has_stripe_test_secret_key || paymentSettings.value.stripe_test_secret_key)

  if (!paymentSettings.value.is_active) issues.push('Stripe payments are disabled.')
  if (!hasActiveKey) issues.push(`Missing ${mode} Stripe secret key.`)
  if (plans.value.filter((plan) => plan.is_active).length === 0) issues.push('No active public plans are available.')
  if (activePaidPlansMissingStripe.value.length > 0) {
    issues.push(`${activePaidPlansMissingStripe.value.length} active paid plan(s) need Stripe price IDs.`)
  }

  return issues
})

const paidOrders = computed(() => orders.value.filter((order) => order.status === 'paid'))
const pendingOrders = computed(() => orders.value.filter((order) => order.status === 'pending'))
const paidRevenueSek = computed(() => paidOrders.value.reduce((sum, order) => sum + Number(order.amount_sek || 0), 0))

const setActiveTab = (tab: AdminTab) => {
  activeTab.value = tab
  router.replace({ path: '/admin', query: { ...route.query, tab } })
}

const showSuccess = (title: string, text?: string) => Swal.fire({
  icon: 'success',
  title,
  text,
  confirmButtonColor: '#2563eb'
})

const showError = (title: string, text?: string) => Swal.fire({
  icon: 'error',
  title,
  text,
  confirmButtonColor: '#2563eb'
})

const showWarning = (title: string, text?: string) => Swal.fire({
  icon: 'warning',
  title,
  text,
  confirmButtonColor: '#2563eb'
})

const showConfirm = async (title: string, text?: string, confirmButtonText = 'Confirm') => {
  const result = await Swal.fire({
    icon: 'warning',
    title,
    text,
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#2563eb',
    cancelButtonColor: '#6b7280'
  })

  return result.isConfirmed
}

const getErrorMessage = (error: unknown, fallback: string) => error instanceof Error ? error.message : fallback

const loadModels = async () => {
  modelsLoading.value = true
  try {
    models.value = await adminService.getModels()
  } catch (error) {
    console.error('Failed to load models:', error)
  } finally {
    modelsLoading.value = false
  }
}

const loadCompanies = async () => {
  companiesLoading.value = true
  try {
    companies.value = await adminService.getCompaniesWithModels()
  } catch (error) {
    console.error('Failed to load companies:', error)
  } finally {
    companiesLoading.value = false
  }
}

const editModel = (model: AIModel) => {
  modelToEdit.value = model
}

const closeModelModal = () => {
  showAddModelModal.value = false
  modelToEdit.value = null
}

const confirmDeleteModel = (model: AIModel) => {
  modelToDelete.value = model
}

const handleDeleteModel = async () => {
  if (!modelToDelete.value) return

  try {
    await adminService.deleteModel(modelToDelete.value.id)
    await loadModels()
    modelToDelete.value = null
  } catch (error) {
    console.error('Failed to delete model:', error)
    showError('Failed to delete model', getErrorMessage(error, 'Failed to delete model'))
  }
}

const setCompanyModel = async (companyId: number, modelId: string) => {
  if (!modelId) return

  try {
    await adminService.setCompanyModel(companyId, parseInt(modelId))
    await loadCompanies()
  } catch (error) {
    console.error('Failed to set company model:', error)
    showError('Failed to set company model', getErrorMessage(error, 'Failed to set company model'))
  }
}

const openCompanyEditor = (company: Company) => {
  const ownerName = splitOwnerName(company.owner_name)
  editingCompany.value = company
  companyEditForm.value = {
    name: company.name || '',
    domain: company.domain || '',
    industry: company.industry || '',
    owner_first_name: ownerName.first,
    owner_last_name: ownerName.last,
    owner_email: company.owner_email || '',
    billing_email: company.billing_email || company.owner_email || '',
    max_agents: Number(company.max_agents ?? 5),
    subscription_status: (company.subscription_status as UpdateCompanyDetailsRequest['subscription_status']) || 'trial',
    is_active: company.is_active !== false
  }
}

const closeCompanyEditor = () => {
  editingCompany.value = null
  companyEditForm.value = defaultCompanyEditForm()
}

const saveCompanyDetails = async () => {
  if (!editingCompany.value) return

  try {
    await adminService.updateCompanyDetails(editingCompany.value.id, {
      name: companyEditForm.value.name?.trim(),
      domain: companyEditForm.value.domain?.trim(),
      industry: companyEditForm.value.industry?.trim() || null,
      owner_first_name: companyEditForm.value.owner_first_name?.trim() || null,
      owner_last_name: companyEditForm.value.owner_last_name?.trim() || null,
      owner_email: companyEditForm.value.owner_email?.trim() || null,
      billing_email: companyEditForm.value.billing_email?.trim() || null,
      max_agents: Number(companyEditForm.value.max_agents ?? 5),
      subscription_status: companyEditForm.value.subscription_status,
      is_active: Boolean(companyEditForm.value.is_active)
    })
    await loadCompanies()
    closeCompanyEditor()
    showSuccess('Company details updated')
  } catch (error) {
    showError('Failed to update company details', getErrorMessage(error, 'Failed to update company details'))
  }
}

const toggleCompanyActive = async (company: Company) => {
  const nextActive = company.is_active === false
  const action = nextActive ? 'activate' : 'deactivate'
  const confirmed = await showConfirm(
    `${nextActive ? 'Activate' : 'Deactivate'} company?`,
    `Are you sure you want to ${action} ${company.name}?`,
    nextActive ? 'Activate' : 'Deactivate'
  )
  if (!confirmed) return

  try {
    await adminService.updateCompanyStatus(company.id, {
      is_active: nextActive,
      subscription_status: nextActive ? company.subscription_status || 'active' : 'suspended'
    })
    await loadCompanies()
  } catch (error) {
    console.error('Failed to update company:', error)
    showError('Failed to update company', getErrorMessage(error, 'Failed to update company'))
  }
}

const loadEmailSettings = async () => {
  const settings = await adminService.getEmailSettings()
  if (settings) emailSettings.value = { ...emailSettings.value, ...settings }
}

const loadSmtpServers = async () => {
  try {
    smtpServers.value = await adminService.getSmtpServers()
  } catch (error) {
    console.error('Failed to load SMTP servers:', error)
  }
}

const saveSmtpServer = async () => {
  try {
    if (!smtpForm.value.name || !smtpForm.value.host || !smtpForm.value.username || !smtpForm.value.from_email) {
      showWarning('Missing SMTP details', 'Name, host, username, and from email are required')
      return
    }

    if (editingSmtpServer.value) {
      await adminService.updateSmtpServer(editingSmtpServer.value.id, smtpForm.value)
      editingSmtpServer.value = null
    } else {
      await adminService.createSmtpServer(smtpForm.value)
    }

    smtpForm.value = defaultSmtpForm()
    await loadSmtpServers()
    await loadEmailSettings()
    showSuccess('SMTP server saved')
  } catch (error) {
    showError('Failed to save SMTP server', getErrorMessage(error, 'Failed to save SMTP server'))
  }
}

const editSmtpServer = (server: SmtpServer) => {
  editingSmtpServer.value = server
  smtpForm.value = {
    name: server.name,
    host: server.host,
    port: server.port,
    username: server.username,
    password: '',
    encryption: server.encryption,
    from_email: server.from_email,
    from_name: server.from_name,
    reply_to_email: server.reply_to_email || '',
    is_active: server.is_active
  }
}

const cancelEditSmtpServer = () => {
  editingSmtpServer.value = null
  smtpForm.value = defaultSmtpForm()
}

const activateSmtpServer = async (server: SmtpServer) => {
  try {
    await adminService.activateSmtpServer(server.id)
    await loadSmtpServers()
    await loadEmailSettings()
  } catch (error) {
    showError('Failed to activate SMTP server', getErrorMessage(error, 'Failed to activate SMTP server'))
  }
}

const deactivateSmtpServer = async (server: SmtpServer) => {
  try {
    await adminService.deactivateSmtpServer(server.id)
    await loadSmtpServers()
    await loadEmailSettings()
  } catch (error) {
    showError('Failed to deactivate SMTP server', getErrorMessage(error, 'Failed to deactivate SMTP server'))
  }
}

const deleteSmtpServer = async (server: SmtpServer) => {
  if (!await showConfirm('Delete SMTP server?', `Delete SMTP server "${server.name}"?`, 'Delete')) return
  try {
    await adminService.deleteSmtpServer(server.id)
    await loadSmtpServers()
    await loadEmailSettings()
  } catch (error) {
    showError('Failed to delete SMTP server', getErrorMessage(error, 'Failed to delete SMTP server'))
  }
}

const saveEmailSettings = async () => {
  try {
    await adminService.saveEmailSettings(emailSettings.value)
    emailSettings.value.sendgrid_api_key = ''
    emailSettings.value.smtp_password = ''
    await loadEmailSettings()
    showSuccess('Email settings saved')
  } catch (error) {
    showError('Failed to save email settings', getErrorMessage(error, 'Failed to save email settings'))
  }
}

const sendTestEmail = async () => {
  if (!testEmailTo.value) return
  try {
    await adminService.sendTestEmail(testEmailTo.value)
    showSuccess('Test email sent')
  } catch (error) {
    showError('Failed to send test email', getErrorMessage(error, 'Failed to send test email'))
  }
}

const createCheckout = async (company: Company) => {
  try {
    const url = await adminService.createStripeCheckout(company.id)
    window.open(url, '_blank', 'noopener,noreferrer')
  } catch (error) {
    showError('Failed to create Stripe checkout', getErrorMessage(error, 'Failed to create Stripe checkout'))
  }
}

const markCompanyPaid = async (company: Company) => {
  const confirmed = await showConfirm(
    'Mark company as paid?',
    `${company.name} will be activated for one month without Stripe payment. Use this only when payment has already been confirmed externally.`,
    'Mark paid'
  )
  if (!confirmed) return

  try {
    await adminService.updateCompanyStatus(company.id, {
      mark_paid: true,
      billing_period: 'monthly',
      subscription_status: 'active',
      is_active: true
    })
    await loadCompanies()
    showSuccess('Company marked as paid and active')
  } catch (error) {
    showError('Failed to mark company paid', getErrorMessage(error, 'Failed to mark company paid'))
  }
}

const sendRenewalReminder = async (company: Company) => {
  try {
    await adminService.sendRenewalReminder(company.id)
    showSuccess('Renewal reminder sent')
  } catch (error) {
    showError('Failed to send renewal reminder', getErrorMessage(error, 'Failed to send renewal reminder'))
  }
}

const runBillingSchedulerNow = async () => {
  try {
    const headers = await adminService['getAuthHeaders']()
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.deepchat.se'}/api/admin/run-billing-scheduler`, {
      method: 'POST', headers
    })
    const data = await response.json() as any
    showSuccess(data.message || 'Billing scheduler executed')
    await loadCompanies()
  } catch (error) {
    showError('Failed to run billing scheduler', getErrorMessage(error, 'Failed to run billing scheduler'))
  }
}

const updateMaxAgents = async (company: Company, maxAgents: number) => {
  try {
    await adminService.updateCompanyStatus(company.id, { max_agents: maxAgents })
    await loadCompanies()
  } catch (error) {
    showError('Failed to update agent limit', getErrorMessage(error, 'Failed to update agent limit'))
  }
}

const sendTrialReminder = async (company: Company) => {
  try {
    await adminService.sendTrialReminder(company.id)
    showSuccess('Trial upgrade reminder sent')
  } catch (error) {
    showError('Failed to send trial reminder', getErrorMessage(error, 'Failed to send trial reminder'))
  }
}

// SMS Gateway
const smsProviders = ref<SmsProvider[]>([])
const smsLogs = ref<SmsLog[]>([])
const editingSmsProvider = ref<SmsProvider | null>(null)
const testSmsPhone = ref('')
type SmsProviderForm = Omit<SmsProvider, 'id' | 'last_success_at' | 'last_error' | 'created_at' | 'updated_at'>

const defaultSmsForm = (): SmsProviderForm => ({
  name: '', api_url: '', http_method: 'POST', content_type: 'application/json',
  phone_param: 'phone', message_param: 'message', phone_prefix: '+46',
  extra_params: '{}', auth_header: '', is_active: true, priority: 10
})
const smsForm = ref(defaultSmsForm())

const loadSmsProviders = async () => {
  try { smsProviders.value = await adminService.getSmsProviders() } catch (e) { console.error(e) }
}

const loadSmsLogs = async () => {
  try {
    const result = await adminService.getSmsLogs()
    smsLogs.value = result.data
  } catch (e) { console.error(e) }
}

const saveSmsProvider = async () => {
  try {
    if (editingSmsProvider.value) {
      await adminService.updateSmsProvider(editingSmsProvider.value.id, smsForm.value)
      editingSmsProvider.value = null
    } else {
      await adminService.createSmsProvider(smsForm.value)
    }
    smsForm.value = defaultSmsForm()
    await loadSmsProviders()
    showSuccess('SMS provider saved')
  } catch (error) {
    showError('Failed to save SMS provider', getErrorMessage(error, 'Failed to save SMS provider'))
  }
}

const editSmsProvider = (provider: SmsProvider) => {
  editingSmsProvider.value = provider
  smsForm.value = {
    name: provider.name, api_url: provider.api_url,
    http_method: provider.http_method, content_type: provider.content_type,
    phone_param: provider.phone_param, message_param: provider.message_param,
    phone_prefix: provider.phone_prefix, extra_params: provider.extra_params,
    auth_header: provider.auth_header || '', is_active: provider.is_active, priority: provider.priority
  }
}

const cancelEditSmsProvider = () => {
  editingSmsProvider.value = null
  smsForm.value = defaultSmsForm()
}

const deleteSmsProvider = async (provider: SmsProvider) => {
  if (!await showConfirm('Delete SMS provider?', `Delete SMS provider "${provider.name}"?`, 'Delete')) return
  try {
    await adminService.deleteSmsProvider(provider.id)
    await loadSmsProviders()
  } catch (error) {
    showError('Failed to delete provider', getErrorMessage(error, 'Failed to delete provider'))
  }
}

const testSms = async (provider: SmsProvider) => {
  if (!testSmsPhone.value) { showWarning('Phone number required', 'Enter a phone number to test'); return }
  try {
    await adminService.testSmsProvider(provider.id, testSmsPhone.value)
    showSuccess(`Test SMS sent via ${provider.name}`)
    await loadSmsLogs()
  } catch (error) {
    showError('Test SMS failed', getErrorMessage(error, 'Test SMS failed'))
    await loadSmsLogs()
  }
}

const loadEmailTemplates = async () => {
  emailTemplates.value = await adminService.getEmailTemplates()
}

const saveTemplate = async (template: EmailTemplate) => {
  try {
    await adminService.saveEmailTemplate(template)
    showSuccess('Template saved')
  } catch (error) {
    showError('Failed to save template', getErrorMessage(error, 'Failed to save template'))
  }
}

const loadPaymentSettings = async () => {
  const settings = await adminService.getPaymentSettings()
  if (settings) paymentSettings.value = { ...paymentSettings.value, ...settings }
}

const loadPlans = async () => {
  plansLoading.value = true
  try {
    plans.value = await adminService.getPlans()
  } catch (error) {
    showError('Failed to load plans', getErrorMessage(error, 'Failed to load plans'))
  } finally {
    plansLoading.value = false
  }
}

const loadOrders = async () => {
  ordersLoading.value = true
  try {
    orders.value = await adminService.getOrders()
  } catch (error) {
    showError('Failed to load orders', getErrorMessage(error, 'Failed to load orders'))
  } finally {
    ordersLoading.value = false
  }
}

const startCreatePlan = () => {
  editingPlanId.value = null
  planForm.value = defaultPlanForm()
  planFeaturesText.value = ''
  showPlanForm.value = true
}

const startEditPlan = (plan: SubscriptionPlan) => {
  editingPlanId.value = plan.id
  planForm.value = {
    name: plan.name,
    description: plan.description || '',
    price_sek: plan.price_sek,
    billing_period: plan.billing_period,
    max_agents: plan.max_agents,
    features: [...plan.features],
    stripe_price_id: plan.stripe_price_id || '',
    is_active: plan.is_active,
    sort_order: plan.sort_order
  }
  planFeaturesText.value = plan.features.join('\n')
  showPlanForm.value = true
}

const cancelPlanEdit = () => {
  editingPlanId.value = null
  planForm.value = defaultPlanForm()
  planFeaturesText.value = ''
  showPlanForm.value = false
}

const buildPlanPayload = (): SubscriptionPlanPayload => ({
  ...planForm.value,
  price_sek: Number(planForm.value.price_sek || 0),
  max_agents: Number(planForm.value.max_agents || 0),
  sort_order: Number(planForm.value.sort_order || 0),
  stripe_price_id: planForm.value.stripe_price_id?.trim() || undefined,
  features: planFeaturesText.value
    .split('\n')
    .map((feature) => feature.trim())
    .filter(Boolean)
})

const savePlan = async () => {
  plansSaving.value = true
  try {
    const payload = buildPlanPayload()
    if (editingPlanId.value) {
      await adminService.updatePlan(editingPlanId.value, payload)
      showSuccess('Plan updated')
    } else {
      await adminService.createPlan(payload)
      showSuccess('Plan created')
    }
    cancelPlanEdit()
    await loadPlans()
  } catch (error) {
    showError('Failed to save plan', getErrorMessage(error, 'Failed to save plan'))
  } finally {
    plansSaving.value = false
  }
}

const deletePlan = async (plan: SubscriptionPlan) => {
  if (!await showConfirm('Delete plan?', `Delete "${plan.name}"? Companies using this plan may be affected.`, 'Delete')) return
  try {
    await adminService.deletePlan(plan.id)
    await loadPlans()
    showSuccess('Plan deleted')
  } catch (error) {
    showError('Failed to delete plan', getErrorMessage(error, 'Failed to delete plan'))
  }
}

const savePaymentSettings = async () => {
  try {
    await adminService.savePaymentSettings(paymentSettings.value)
    paymentSettings.value.stripe_test_secret_key = ''
    paymentSettings.value.stripe_live_secret_key = ''
    paymentSettings.value.stripe_secret_key = ''
    paymentSettings.value.stripe_webhook_secret = ''
    await loadPaymentSettings()
    showSuccess('Payment settings saved')
  } catch (error) {
    showError('Failed to save payment settings', getErrorMessage(error, 'Failed to save payment settings'))
  }
}

const impersonateCompany = async (company: Company) => {
  const confirmed = await showConfirm(
    'Access company dashboard?',
    `You are about to access ${company.name} as their admin. You will see everything they see.`,
    'Access'
  )
  if (!confirmed) return

  try {
    const result = await adminService.accessCompany(company.id)
    
    const companyPortalUrl = import.meta.env.VITE_COMPANY_PORTAL_URL || 'https://app.deepchat.se'
    window.location.href = `${companyPortalUrl}/dashboard?impersonationToken=${encodeURIComponent(result.token)}`
  } catch (error) {
    console.error('Failed to impersonate:', error)
    showError('Failed to impersonate company', getErrorMessage(error, 'Failed to impersonate company'))
  }
}

onMounted(() => {
  loadModels()
  loadEmailSettings()
  loadSmtpServers()
  loadEmailTemplates()
  loadPaymentSettings()
  loadPlans()
  loadOrders()
  if (activeTab.value === 'companies') {
    loadCompanies()
  }
})

// Watch tab changes
watch(() => route.query.tab, () => {
  activeTab.value = getRouteTab()
})

watch(activeTab, (newTab) => {
  if (newTab === 'companies' && companies.value.length === 0) {
    loadCompanies()
  }
  if (newTab === 'templates' && emailTemplates.value.length === 0) {
    loadEmailTemplates()
  }
  if (newTab === 'sms' && smsProviders.value.length === 0) {
    loadSmsProviders()
    loadSmsLogs()
  }
  if (newTab === 'payments') {
    if (plans.value.length === 0) loadPlans()
    loadOrders()
  }
})
</script>
